import React, { useEffect, useState, useCallback, useMemo } from 'react';
import Select from 'react-select';
import { toast } from 'react-toastify';
import ApiService from '../../services/apiService';
import Loader from '../../elements/Loader';

function stripHtml(html) {
  if (!html) return '';
  return html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
}

function fmtDate(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC',
  });
}

export default function FestivalPujas() {
  const [festivals, setFestivals]       = useState([]);
  const [selected, setSelected]         = useState(null);
  const [linkedPujas, setLinkedPujas]   = useState([]);
  const [pujaOptions, setPujaOptions]   = useState([]);
  const [selectedPuja, setSelectedPuja] = useState(null);

  const [festivalSearch, setFestivalSearch] = useState('');
  const [loadingFestivals, setLoadingFestivals] = useState(false);
  const [loadingPujas, setLoadingPujas]         = useState(false);
  const [loadingLinked, setLoadingLinked]       = useState(false);
  const [saving, setSaving]                     = useState(false);
  const [removingId, setRemovingId]             = useState(null);

  /* ── fetch all festivals ── */
  const fetchFestivals = useCallback(async () => {
    setLoadingFestivals(true);
    try {
      const res = await ApiService.get('festival/all');
      setFestivals(res?.data || []);
    } catch {
      toast.error('Failed to load festivals');
    } finally {
      setLoadingFestivals(false);
    }
  }, []);

  /* ── fetch all pujas for the dropdown ── */
  const fetchPujaOptions = useCallback(async () => {
    setLoadingPujas(true);
    try {
      const res = await ApiService.get('service/list');
      const list = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
      setPujaOptions(list.map(s => ({ value: s.id, label: s.title || s.name || `Puja #${s.id}` })));
    } catch {
      toast.error('Failed to load puja list');
    } finally {
      setLoadingPujas(false);
    }
  }, []);

  /* ── fetch pujas linked to the selected festival ── */
  const fetchLinkedPujas = useCallback(async (festivalId) => {
    setLoadingLinked(true);
    setLinkedPujas([]);
    try {
      const res = await ApiService.get(`festival/${festivalId}/pujas`);
      setLinkedPujas(res?.pujas || []);
    } catch {
      toast.error('Failed to load linked pujas');
    } finally {
      setLoadingLinked(false);
    }
  }, []);

  useEffect(() => {
    fetchFestivals();
    fetchPujaOptions();
  }, [fetchFestivals, fetchPujaOptions]);

  const handleSelectFestival = (festival) => {
    setSelected(festival);
    setSelectedPuja(null);
    fetchLinkedPujas(festival.id);
  };

  /* ── link a puja ── */
  const handleAdd = async () => {
    if (!selectedPuja) { toast.error('Please select a puja first'); return; }
    setSaving(true);
    try {
      await ApiService.post(`festival/${selected.id}/pujas`, { pujaId: selectedPuja.value });
      toast.success(`"${selectedPuja.label}" linked to ${selected.name}`);
      setSelectedPuja(null);
      fetchLinkedPujas(selected.id);
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Failed to link puja';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  /* ── unlink a puja ── */
  const handleRemove = async (pujaId, pujaTitle) => {
    if (!window.confirm(`Remove "${pujaTitle}" from ${selected.name}?`)) return;
    setRemovingId(pujaId);
    try {
      await ApiService.delete(`festival/${selected.id}/pujas/${pujaId}`);
      toast.success(`"${pujaTitle}" removed`);
      fetchLinkedPujas(selected.id);
    } catch {
      toast.error('Failed to remove puja');
    } finally {
      setRemovingId(null);
    }
  };

  const filteredFestivals = useMemo(() => {
    const q = festivalSearch.toLowerCase();
    return festivals.filter(f => f.name?.toLowerCase().includes(q));
  }, [festivals, festivalSearch]);

  /* ── pujas available to add (exclude already linked) ── */
  const linkedIds = new Set(linkedPujas.map(p => p.id));
  const availableOptions = pujaOptions.filter(o => !linkedIds.has(o.value));

  return (
    <div className="container-fluid">

      {/* Header */}
      <div className="mb-4">
        <h3 className="mb-0">Festival Puja Management</h3>
        <small className="text-muted">Select a festival on the left to manage which pujas are linked to it</small>
      </div>

      <div className="row">

        {/* LEFT — Festival list */}
        <div className="col-xl-4 col-lg-5 mb-4">
          <div className="card h-100">
            <div className="card-header border-0 pb-2">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h5 className="card-title mb-0">Festivals</h5>
                {festivalSearch && <small className="text-muted">{filteredFestivals.length} of {festivals.length}</small>}
              </div>
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Search festivals..."
                value={festivalSearch}
                onChange={e => setFestivalSearch(e.target.value)}
                style={{ width: '60%' }}
              />
            </div>
            <div className="card-body p-0" style={{ maxHeight: 580, overflowY: 'auto' }}>
              {loadingFestivals && <div className="p-3"><Loader /></div>}
              {!loadingFestivals && filteredFestivals.length === 0 && (
                <p className="text-muted text-center mt-4 px-3">
                  {festivalSearch ? 'No matching festivals found.' : 'No festivals found. Add one from'} {!festivalSearch && <strong>Puja Category</strong>}
                </p>
              )}
              {filteredFestivals.map(f => {
                const isActive = selected?.id === f.id;
                return (
                  <div
                    key={f.id}
                    onClick={() => handleSelectFestival(f)}
                    style={{
                      padding: '12px 16px',
                      cursor: 'pointer',
                      borderBottom: '1px solid #f0f0f0',
                      background: isActive ? '#f4f0ff' : 'transparent',
                      borderLeft: isActive ? '3px solid #6c5ffc' : '3px solid transparent',
                      transition: 'background 0.15s',
                    }}
                  >
                    <div className="d-flex align-items-center gap-2">
                      {f.image ? (
                        <img src={f.image} alt={f.name}
                          style={{ width: 36, height: 36, borderRadius: 6, objectFit: 'cover', flexShrink: 0 }} />
                      ) : (
                        <div style={{
                          width: 36, height: 36, borderRadius: 6, background: '#6c5ffc22',
                          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                        }}>
                          <i className="fa fa-star" style={{ color: '#6c5ffc', fontSize: 16 }} />
                        </div>
                      )}
                      <div style={{ minWidth: 0 }}>
                        <div className="fw-semibold text-truncate" style={{ fontSize: 14 }}>{f.name}</div>
                        <div className="text-muted" style={{ fontSize: 11 }}>
                          {fmtDate(f.festiveDate)}
                          {f.festiveEndDate && f.festiveEndDate !== f.festiveDate
                            ? ` → ${fmtDate(f.festiveEndDate)}` : ''}
                        </div>
                      </div>
                      <span className={`badge ms-auto ${f.isActive ? 'badge-success' : 'badge-secondary'}`}
                        style={{ fontSize: 10, flexShrink: 0 }}>
                        {f.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* RIGHT — Linked pujas */}
        <div className="col-xl-8 col-lg-7 mb-4">
          {!selected ? (
            <div className="card h-100 d-flex align-items-center justify-content-center"
              style={{ minHeight: 300 }}>
              <div className="text-center text-muted p-4">
                <i className="fa fa-hand-o-left fa-2x mb-3" style={{ opacity: 0.3 }} />
                <p className="mb-0">Select a festival to manage its pujas</p>
              </div>
            </div>
          ) : (
            <div className="card">
              <div className="card-header border-0 pb-0">
                <div className="d-flex align-items-center justify-content-between">
                  <div>
                    <h5 className="card-title mb-0">{selected.name}</h5>
                    <small className="text-muted">
                      {fmtDate(selected.festiveDate)}
                      {selected.festiveEndDate && selected.festiveEndDate !== selected.festiveDate
                        ? ` → ${fmtDate(selected.festiveEndDate)}` : ''}
                      &nbsp;·&nbsp;{linkedPujas.length} puja{linkedPujas.length !== 1 ? 's' : ''} linked
                    </small>
                  </div>
                </div>
              </div>

              <div className="card-body">

                {/* Add puja row */}
                <div className="d-flex gap-2 align-items-center mb-4">
                  <div style={{ flex: 1 }}>
                    <Select
                      options={availableOptions}
                      value={selectedPuja}
                      onChange={setSelectedPuja}
                      isLoading={loadingPujas}
                      isSearchable
                      isClearable
                      placeholder="Search and select a puja to link..."
                      classNamePrefix="react-select"
                      styles={{
                        control: b => ({ ...b, minHeight: 42 }),
                        menu: b => ({ ...b, zIndex: 9999 }),
                      }}
                    />
                  </div>
                  <button
                    className="btn btn-primary"
                    onClick={handleAdd}
                    disabled={saving || !selectedPuja}
                    style={{ whiteSpace: 'nowrap' }}
                  >
                    {saving ? <i className="fa fa-spinner fa-spin" /> : '+ Link Puja'}
                  </button>
                </div>

                {/* Linked pujas table */}
                {loadingLinked && <Loader />}
                {!loadingLinked && linkedPujas.length === 0 && (
                  <div className="text-center text-muted py-5">
                    <i className="fa fa-link fa-2x mb-2" style={{ opacity: 0.3 }} /><br />
                    No pujas linked yet. Use the selector above to add some.
                  </div>
                )}
                {!loadingLinked && linkedPujas.length > 0 && (
                  <div className="table-responsive">
                    <table className="table table-hover mb-0">
                      <thead className="table-light">
                        <tr>
                          <th style={{ width: 40 }}>#</th>
                          <th>Puja</th>
                          <th>Price</th>
                          <th>Duration</th>
                          <th style={{ width: 80 }}>Remove</th>
                        </tr>
                      </thead>
                      <tbody>
                        {linkedPujas.map((p, i) => (
                          <tr key={p.id}>
                            <td><strong>{i + 1}</strong></td>
                            <td>
                              <div className="d-flex align-items-center gap-2">
                                {p.image && (
                                  <img src={p.image} alt={p.title}
                                    style={{ width: 32, height: 32, borderRadius: 4, objectFit: 'cover' }} />
                                )}
                                <div>
                                  <div className="fw-semibold" style={{ fontSize: 14 }}>{p.title}</div>
                                  {p.shortDescription && (
                                    <div className="text-muted text-truncate" style={{ fontSize: 11, maxWidth: 280 }}>
                                      {stripHtml(p.shortDescription)}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td style={{ fontSize: 13 }}>
                              {p.price ? `₹${p.price}` : '—'}
                            </td>
                            <td style={{ fontSize: 13 }}>
                              {p.duration || '—'}
                            </td>
                            <td>
                              <button
                                className="btn btn-danger shadow btn-xs sharp"
                                title="Remove from festival"
                                disabled={removingId === p.id}
                                onClick={() => handleRemove(p.id, p.title)}
                              >
                                {removingId === p.id
                                  ? <i className="fa fa-spinner fa-spin" />
                                  : <i className="fa fa-trash" />}
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

      </div>
    </div>
  );
}
